import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';

const ProductDetailPage = () => {
  const history = useHistory();
  // const [activeLevel, setActiveLevel] = useState('L2A');
  const [mapType, setMapType] = useState('satellite'); // 'street' or 'satellite'

  // Define processing level information based on selected level
  // const levelInfo = {
  //   'L1A': {
  //     radiometricLevel: 'Sensor Corrected / RAW',
  //     geometricProcessing: 'Atmsopheric Corrections',
  //     description: 'Level 1A products are raw data with no corrections applied. These are typically used for specialized processing pipelines that require starting with unmodified sensor data. This level includes raw digital numbers directly from the sensor with minimal preprocessing.',
  //   },
  //   'L1B': {
  //     radiometricLevel: 'Top of Atmosphere Reflectance',
  //     geometricProcessing: 'Geometric corrections',
  //     description: 'Level 1B products are radiometrically calibrated to radiance values. Basic geometric corrections for sensor effects have been applied, but the data is not yet georeferenced. This level is useful for users who want to apply their own custom geometric corrections.',
  //   },
  //   'L1C': {
  //     radiometricLevel: 'Bottom of the Atmosphere',
  //     geometricProcessing: 'Georectified',
  //     description: 'Level 1C products are orthorectified to a standard cartographic projection using a digital elevation model (DEM) to correct for terrain effects. These products are geometrically corrected and projected to a standard map projection, making them suitable for most GIS applications.',
  //   },
  //   'L2A': {
  //     radiometricLevel: 'Surface Reflectance',
  //     geometricProcessing: 'Orthorectified',
  //     description: 'Level 2A products are atmospherically corrected to surface reflectance values. These products account for atmospheric effects, providing the most accurate representation of ground features. L2A data is ideal for vegetation monitoring, land cover classification, and other applications requiring accurate spectral information.',
  //   }
  // };

  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 2 }}>
      {/* Back Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{
            color: '#4287f5',
            textTransform: 'none',
            fontWeight: 500
          }}
          onClick={() => history.push('/products')}
        >
          Products &gt; OPT - 70
        </Button>
      </Box>

      {/* Product Description */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
          Product Description
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
          KaleidEO is at the forefront of the Earth Observation Infrastructure-as-a-Service (EO-IaaS) model, with a vision to maximize the value generated from every pixel of EO satellite data.
          Our unique approach to solving EO value chain problems lies in a 'demand-driven' philosophy,
          where we build an integrated infrastructure that seamlessly incorporates data and insights into our clients' business decision-making processes and translate these requirements to
          hardware, data and analysis to create vertical, segment -focused solution, moving beyond the traditional 'one-size-fits-all' imagery approach. KaleidEO has achieved significant
          milestones, including becoming the first private company from India to design, develop, and test a payload prototype through maiden aerial testing and to successfully test edge
          computing in space. Currently, we are advancing the development of a high-resolution, large-swath multispectral Earth observation payload at Technology Readiness Level (TRL) 6.
          Our payload significantly improves the signal-to-noise ratio and Modulation Transfer Frequency (MTF) of captured
          data, resulting in superior image quality.
        </Typography>
      </Box>

      {/* Specifications Table */}
      <TableContainer component={Paper} sx={{ mb: 4, border: '1px solid #d0d7de', boxShadow: 'none' }}>
  <Table>
    <TableHead>
      <TableRow sx={{ backgroundColor: '#f0f4fa' }}>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Geographical Coverage</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Image Resolution</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Source</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Revisit Frequency</TableCell>
        <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Data Format</TableCell>
        {/* <TableCell align="center" sx={{ fontWeight: 600, color: '#1976d2' }}>Unit</TableCell> */}
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow hover>
        <TableCell align="center">Global*</TableCell>
        <TableCell align="center">70 cm x 70 cm Pixel</TableCell>
        <TableCell align="center">OPT - 70</TableCell>
        <TableCell align="center">Daily</TableCell>
        <TableCell align="center">GeoTIFF, .png, .csv</TableCell>
        {/* <TableCell align="center">N/A</TableCell> */}
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ fontStyle: 'italic', fontSize: '0.85rem', color: 'gray', py: 1 }}>
          *Global coverage available. AOI outside India requires additional processing time.
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>

  <Table sx={{ mt: 4 }}>
    <TableHead>
      <TableRow sx={{ backgroundColor: '#f0f4fa' }}>
        <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Spectral Band</TableCell>
        <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Wavelength Range</TableCell>
        <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>Description</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow hover>
        <TableCell>Red (R)</TableCell>
        <TableCell>~ 640–670 nm</TableCell>
        <TableCell>Captures visible red light; useful for vegetation and soil analysis.</TableCell>
      </TableRow>
      <TableRow hover>
        <TableCell>Green (G)</TableCell>
        <TableCell>~ 520–600 nm</TableCell>
        <TableCell>Useful for assessing plant vigor and urban features.</TableCell>
      </TableRow>
      <TableRow hover>
        <TableCell>Blue (B)</TableCell>
        <TableCell>~ 450–500 nm</TableCell>
        <TableCell>Highlights water bodies and atmospheric haze detection.</TableCell>
      </TableRow>
      <TableRow hover>
        <TableCell>Near-Infrared (NIR)</TableCell>
        <TableCell>~ 850–880 nm</TableCell>
        <TableCell>Essential for vegetation health and biomass estimation.</TableCell>
      </TableRow>
      <TableRow hover>
        <TableCell>Red Edge</TableCell>
        <TableCell>~ 705–745 nm</TableCell>
        <TableCell>Detects early plant stress and chlorophyll content.</TableCell>
      </TableRow>
      <TableRow hover>
        <TableCell>Panchromatic (PAN)</TableCell>
        <TableCell>~ 500–800 nm</TableCell>
        <TableCell>Provides high-resolution grayscale imagery used for sharpening.</TableCell>
      </TableRow>
    </TableBody>
  </Table>
</TableContainer>

     

      {/* Processing Level Section - Two Columns */}
      {/* <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 4 }}>
        <Box sx={{ width: { xs: '100%', md: '50%' }, pr: { md: 2 }, mb: { xs: 3, md: 0 } }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {['L1A', 'L1B', 'L1C', 'L2A'].map(level => (
              <Button
                key={level}
                variant={activeLevel === level ? 'contained' : 'outlined'}
                sx={{
                  borderColor: '#ccc',
                  color: activeLevel === level ? 'white' : '#1976d2',
                  bgcolor: activeLevel === level ? '#1976d2' : 'transparent',
                  '&:hover': {
                    bgcolor: activeLevel === level ? '#1565c0' : 'rgba(25, 118, 210, 0.04)'
                  }
                }}
                onClick={() => setActiveLevel(level)}
              >
                {level}
              </Button>
            ))}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Radiometric processing level:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {levelInfo[activeLevel].radiometricLevel}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Geometric processing:
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {levelInfo[activeLevel].geometricProcessing}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {levelInfo[activeLevel].description}
          </Typography>
        </Box>
      </Box> */}

      {/* Order Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Button
          variant="contained"
          sx={{
            bgcolor: '#1976d2',
            color: 'white',
            py: 1,
            px: 4,
            '&:hover': {
              bgcolor: '#1565c0'
            }
          }}
        >
          Order Imagery
        </Button>
      </Box>
    </Box>
  );
};

export default ProductDetailPage;